import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, DollarSign, Bell, Mail, LogOut, ShoppingBag, Gift } from "lucide-react";
import { createPageUrl } from "@/utils";
import { hapticLight } from "@/utils/haptic";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import notificationBell from "@/assets/icon-gravestone.png";
import mailboxIcon from "@/assets/icon-mail-skull.png";

export default function MobileHeaderDropdown({ 
  onOpenNotifications, 
  onOpenMessages,
  onLogout,
  unreadNotifications = 0,
  unreadMessages = 0,
  checkRateLimit
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleAction = (action) => {
    if (checkRateLimit && checkRateLimit()) return;
    hapticLight();
    setOpen(false);
    action();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="neuro-base active:neuro-pressed rounded-lg h-8 px-2 flex items-center gap-1 smooth-transition relative"
          onClick={() => hapticLight()}
        >
          <span className="text-[10px] text-[#909090] font-medium">Menu</span>
          <ChevronDown className={`w-3 h-3 text-[#909090] transition-transform ${open ? 'rotate-180' : ''}`} />
          {/* Badge for unread count */}
          {(unreadNotifications + unreadMessages) > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
              {unreadNotifications + unreadMessages}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-56 bg-[#1a1a1a] border border-[#2a2a2a] shadow-xl z-[10000]"
        sideOffset={8}
      >
        {/* What We Offer */}
        <DropdownMenuItem 
          onClick={() => handleAction(() => navigate(createPageUrl('WhatWeOffer')))}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
        >
          <Gift className="w-5 h-5 text-[#b0b0b0]" />
          <span className="text-sm text-[#d0d0d0]">What We Offer</span>
        </DropdownMenuItem>

        {/* Get Credits - different behavior for native vs web */}
        {typeof window !== 'undefined' && window.Capacitor ? (
          <DropdownMenuItem 
            onClick={() => {
              hapticLight();
              setOpen(false);
              import('sonner').then(({ toast }) => {
                toast.info(
                  "Credits are how you upload, submit to playlists, and more. But we don't sell credits on mobile. Please visit our web store to get credits.\n\nsoundope.com/shop",
                  { duration: 6000 }
                );
              });
            }}
            className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
          >
            <ShoppingBag className="w-5 h-5 text-primary" />
            <span className="text-sm text-primary">Get Credits</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem 
            onClick={() => handleAction(() => navigate(createPageUrl('Shop')))}
            className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
          >
            <ShoppingBag className="w-5 h-5 text-primary" />
            <span className="text-sm text-primary">Get Credits</span>
          </DropdownMenuItem>
        )}

        {/* Cashout */}
        <DropdownMenuItem 
          onClick={() => handleAction(() => navigate('/cashout'))}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
        >
          <DollarSign className="w-5 h-5 text-[#b0b0b0]" />
          <span className="text-sm text-[#d0d0d0]">Cash Out</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[#2a2a2a]" />

        {/* Notifications */}
        <DropdownMenuItem 
          onClick={() => handleAction(onOpenNotifications)}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
        >
          <div className="relative">
            <img src={notificationBell} alt="Notifications" className="w-5 h-5 object-contain" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                {unreadNotifications}
              </span>
            )}
          </div>
          <span className="text-sm text-[#d0d0d0]">Notifications</span>
        </DropdownMenuItem>

        {/* Messages */}
        <DropdownMenuItem 
          onClick={() => handleAction(onOpenMessages)}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
        >
          <div className="relative">
            <img src={mailboxIcon} alt="Messages" className="w-5 h-5 object-contain" />
            {unreadMessages > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                {unreadMessages}
              </span>
            )}
          </div>
          <span className="text-sm text-[#d0d0d0]">Messages</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[#2a2a2a]" />

        {/* Logout */}
        <DropdownMenuItem 
          onClick={() => handleAction(onLogout)}
          className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-[#252525] focus:bg-[#252525]"
        >
          <LogOut className="w-5 h-5 text-[#b0b0b0]" />
          <span className="text-sm text-[#d0d0d0]">Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
